<template>
    <main class="auth">
        <TransitionEffect>
            <WaitlistModal
                v-if="waitlistModal"
                @close="waitlistModal=false"/>
        </TransitionEffect>
        <BlurredBackground
            :image="constants.covers.login"/>
        <section class="form">
            <Heading
                size="xl">
                {{ redirectAction }}
            </Heading>
            <p class="info">{{ siteContent.closedBetaDisclaimer }}</p>

            <template v-if="!isLogin">
                <section>
                    {{ siteContent.closedRegistration }}
                </section>
                <Button class="discordButton"
                    size="fullWidth"
                    @click="waitlistModal=true">
                    Join the waitlist
                </Button>
                <Button class="discordButton"
                    appearance="secondary"
                    size="fullWidth"
                    link="https://discord.com/invite/5mYNMAfuRR">
                    {{ siteContent.joinUsOnDiscord }}
                </Button>
            </template>

            <template v-else>
                <Feedback class="errorFeedback"
                    v-if="isInvalid"
                    type="alert"
                    icon="error">
                    <template slot="body">
                        <p>{{ feedbackMessage }}</p>
                    </template>
                </Feedback>
                <Login v-if="isLogin"
                    :isInvalid="isInvalid"
                    @setInvalid="setErrorMessage"/>
            </template>
            <p class="redirectInfo">
                <span class="newHere">
                    {{ redirectText }}
                </span>
                <a class="redirectLink"
                    tabindex="0"
                    @click="changeView">
                    {{ authAction }}
                </a>
            </p>
        </section>
    </main>
</template>

<script>
    import { mapActions } from 'vuex'
    import VueRecaptcha from 'vue-recaptcha'

    import Login         from '@/components/login/Login'
    import WaitlistModal from '@/components/landing/WaitlistModal'

    export default {
        name: 'Auth',
        components: {
            VueRecaptcha,
            Login,
            WaitlistModal
        },
        data() {
            return {
                waitlistModal: false,
                isLogin: false,
                isInvalid: false,
                feedbackMessage: ''
            }
        },
        computed: {
            authID() {
                return this.auth.id
            },
            authAction() {
                if(this.isLogin) {
                    return this.siteContent.createAccount
                } else {
                    return this.siteContent.login
                }
            },
            redirectText() {
                if(this.isLogin) {
                    return this.siteContent.newHere
                } else {
                    return this.siteContent.alreadyMember
                }
            },
            redirectAction() {
                if(!this.isLogin) {
                    return this.siteContent.createAccount
                } else {
                    return this.siteContent.login
                }
            }
        },
        methods: {
            ...mapActions('authentication',['signUpWithGoogle']),

            signUp() {
                this.signUpWithGoogle()
                this.redirectBasedOnRouter()
            },

            redirectBasedOnRouter() {
                if(localStorage.getItem('previousRoute' !== null)) {
                    this.$router.push(localStorage.getItem('previousRoute'))
                } else {
                    this.$router.push('/')
                }
            },

            setErrorMessage(error) {
                this.feedbackMessage = error
                this.isInvalid = true
            },

            changeView() {
                this.isInvalid = false
                this.isLogin = !this.isLogin
            }
        },
        mounted() {
            if(this.auth.id) {
                this.redirectBasedOnRouter()
            }
        },
        watch: {
            authID() {
                this.redirectBasedOnRouter()
            }
        },
        metaInfo: {
            title: 'Login | Creatillo'
        }
    }
</script>

<style scoped lang="scss">
    @import '../scss/variables';

    .auth {
        display: flex;
        justify-content: center;
        align-items: center;
        flex-grow: 1;
        padding: 1.5rem 1rem;
        max-height: 100vh;
    }

    .form {
        position: relative;
        padding: 3rem 2rem;
        display: flex;
        flex-direction: column;
        align-items: flex-start;
        width: 100%;
        max-width: 40rem;
        height: max-content;
        background: var(--color-white-40);
        backdrop-filter: blur(2rem);
        border-radius: 1.5rem;
        overflow: hidden;
    }

    .h1 {
        margin: 0 0 0.5rem 0;
    }

    .info {
        font-size: 1rem;
        margin: 0 0 2rem 0;
        color: var(--color-grey-600);
    }

    .captchaContainer {
        width: 100%;
    }

    .otherOptions {
        display: flex;
        width: 100%;
        align-items: center;
        padding: 3rem 0 2rem 0;

        .text {
            width: 100%;
            text-align: center;
            color: var(--color-grey-600);
            font-size: 1rem;
            padding: 0 1rem;
            white-space: nowrap;
        }
        
        .line {
            height: 1px;
            width: 100%;
            background: var(--color-grey-400);
        }
    }

    .icon {
        width: 1.5rem;
        height: 1.5rem;
        margin-right: 1rem;
    }

    .redirectInfo {
        display: flex;
        margin: 2rem auto 0 auto;
        font-size: 1.15rem;
    }

    .redirectLink {
        display: flex;
        align-items: center;
        font-weight: 600;
        font-family: $font-secondary;
        cursor: pointer;
    }

    .newHere {
        margin-right: 1rem;
    }

    .errorFeedback {
        margin-bottom: 2rem;
    }

    .discordButton {
        margin-top: 1rem;
    }

    @media all and (max-width: 1024px) {
        .form {
            padding: 2rem 1.5rem;
        }
    }

    @media all and (max-width: 640px) {
        .auth {
            padding: 2rem 0 0 0;
        }

        .form {
            max-width: 100%;
            border-radius: 1rem 1rem 0 0;
            padding: 2rem 1rem;
            align-self: flex-end;
            padding-bottom: calc(6rem + env(safe-area-inset-bottom));
            height: 100%;
        }
    }
</style>

<style lang="scss">
    .grecaptcha-badge {
        display: none !important;
    }
</style>